import { useMemo } from 'react'

import { useIntl } from 'react-intl'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'
import { convertTZ, useIntlDateFormatter } from 'lib/datetime/dateUtils'

import { useCruiseOfferInclusions } from 'hooks/Cruise/useCruiseOfferInclusions'

import BodyText from 'LuxKit/Typography/BodyText'
import Divider from 'LuxKit/Divider'
import Heading from 'LuxKit/Typography/Heading'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import TextButton from 'LuxKit/Button/TextButton'

import Group from 'components/utils/Group'
import CruiseEvergreenInclusionList from 'components/Cruises/CruiseInclusion/CruiseEvergreenInclusionList'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import CruiseLuxPlusOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseLuxPlusOnboardCreditLabel'

import CruiseDepartureTilePrices from './CruiseDepartureTilePrices'
import CruiseStandardInclusionLabel from 'components/Cruises/CruiseInclusion/CruiseStandardInclusionLabel'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'

interface Props {
  offer: App.CruiseOffer
  departure: App.CruiseDepartureDetails
  onHandleDepartureModal: (departure: App.CruiseDepartureDetails) => void
}

function CruiseDepartureTile({
  offer,
  departure,
  onHandleDepartureModal,
}: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const { standardInclusions, luxPlusInclusions } = useCruiseOfferInclusions({ departure })

  const pricesByCabinCategory = offer.lowestDeparturePricesByCategory[departure.id]
  const isDepartureSoldOut = !pricesByCabinCategory
  const hasInclusions = [
    luxPlusInclusions.length,
    standardInclusions.length,
    offer.evergreenInclusions?.length,
  ].some(Boolean)

  const {
    departureLabel,
    departureWeekdayLabel,
    arrivalLabel,
    arrivalWeekdayLabel,
  } = useMemo(() => {
    // Departure and Arrival dates are related to the port timezone, but the timezone is not provided.
    // To enforce the date to stay the same even in negative timezones (such as Americas), we convert the date to UTC
    const departureDate = convertTZ(new Date(departure.departureDate!), 'UTC')
    const arrivalDate = convertTZ(new Date(departure.arrivalDate!), 'UTC')

    return {
      departureLabel: intlDateFormatter(departureDate, INTL_DMY_CASUAL_FORMAT),
      departureWeekdayLabel: intlDateFormatter(departureDate, INTL_LONG_WEEKDAY),
      arrivalLabel: intlDateFormatter(arrivalDate, INTL_DMY_CASUAL_FORMAT),
      arrivalWeekdayLabel: intlDateFormatter(arrivalDate, INTL_LONG_WEEKDAY),
    }
  }, [departure.departureDate, departure.arrivalDate, intlDateFormatter])

  return <>
    <Group
      direction="vertical"
      desktopDirection="horizontal"
      gap={32}
      horizontalAlign="space-between"
      verticalAlign="center"
    >
      <Group direction="vertical" gap={12}>
        <Group direction="horizontal" gap={12}>
          <div>
            <Heading variant="heading4" wrap="no-wrap">
              {departureLabel}
            </Heading>
            <BodyText variant="small" weight="normal" colour="neutral-three">
              {departureWeekdayLabel}
            </BodyText>
          </div>

          <LineArrowRightIcon />

          <div>
            <Heading variant="heading4" wrap="no-wrap">
              {arrivalLabel}
            </Heading>
            <BodyText variant="small" weight="normal" colour="neutral-three">
              {arrivalWeekdayLabel}
            </BodyText>
          </div>
        </Group>

        {hasInclusions && <Group
          gap={8}
          direction="vertical"
          desktopGap={16}
          horizontalAlign="start"
        >
          <CruiseEvergreenInclusionList
            evergreenInclusions={offer.evergreenInclusions}
          />

          <CruiseStandardInclusionLabel
            standardInclusions={standardInclusions}
            allInclusions
          />

          <CruiseLuxPlusOnboardCreditLabel
            luxPlusInclusions={luxPlusInclusions}
            offer={offer}
            isModalPrimaryActionHidden
          />
        </Group>}

        <PriceRowAgentHubCommission
          checkIn={departure.departureDate ?? undefined}
          checkOut={departure.arrivalDate ?? undefined}
          optionId={departure.id ?? undefined}
          offerId={offer.id}
          offer={offer}
          productType={offer.productType}
        />
        <CruiseLoyaltyPoints
          offer={offer}
          calculationType="estimate"
        />
      </Group>

      <CruiseDepartureTilePrices
        offer={offer}
        departure={departure}
      />

      {!isDepartureSoldOut && <TextButton
        kind="primary"
        onClick={() => onHandleDepartureModal(departure)}
      >
        {intl.formatMessage({
          id: 'qfwVLT0qN37wjcCyMAow',
          defaultMessage: 'Select',
          description: 'Button label to select a cruise departure in the cruise section',
        })}
      </TextButton>}

    </Group>
    <Divider kind="secondary" />
  </>
}

export default CruiseDepartureTile
